<template>
  <div
    v-if="totalPages > 0"
    class="pagination"
  >
    <div class="pagination__mobile-prev-next">
      <button
        :disabled="onFirstPage"
        class="pagination__mobile-prev"
        @click="prevPage"
      >
        {{ $t('common.pagination.previous') }}
      </button>
      <button
        :disabled="onLastPage"
        class="pagination__mobile-next"
        @click="nextPage"
      >
        {{ $t('common.pagination.next') }}
      </button>
    </div>

    <div class="pagination__wrapper">
      <slot
        :start="start + 1"
        :end="end"
        :total="totalItems"
      >
        <div class="pagination__info">
          {{ $tc('common.pagination.info', totalItems, { start: (start + 1), end: end, totalItems: totalItems }) }}
        </div>
      </slot>

      <div v-if="totalPages > 1">
        <nav class="pagination__buttons">
          <pagination-button
            v-for="(button, key) in buttons"
            :key="key"
            v-bind="button"
            @page-change="$emit('page-change', button.page + 1)"
          />
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationButton from './pagination-button'

export default {
  name: 'PaginationBase',

  components: {
    PaginationButton
  },

  props: {
    page: {
      type: Number,
      default: 0,
      validator: (page) => {
        return page >= 0
      }
    },

    itemsPerPage: {
      type: Number,
      default: 25,
      validator: (itemsPerPage) => {
        return itemsPerPage > 0
      }
    },

    maxVisiblePages: {
      type: Number,
      default: 3,
      validator: (maxVisiblePages) => {
        return maxVisiblePages > 0
      }
    },

    totalItems: {
      type: Number,
      required: true,
      validator: (totalItems) => {
        return totalItems >= 0
      }
    },

    loading: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    start () {
      return this.page * this.itemsPerPage
    },

    end () {
      const end = this.start + this.itemsPerPage

      return this.totalItems < end ? this.totalItems : end
    },

    currentPage () {
      return this.page + 1
    },

    onFirstPage () {
      return this.currentPage == 1
    },

    onLastPage () {
      return this.currentPage == this.totalPages
    },

    totalPages () {
      if (this.itemsPerPage === 0) {
        return 0
      }

      return Math.ceil(this.totalItems / this.itemsPerPage)
    },

    pages () {
      let pages = []
      const filteredPages = this.filteredPages

      if (filteredPages) {
        pages = [
          filteredPages[0] - 1 === 1 ? 1 : '...',
          ...filteredPages,
          filteredPages[filteredPages.length - 1] + 1 === this.totalPages - 2 ? this.totalPages - 2 : '...'
        ]
      } else {
        pages = [
          ...Array(this.totalPages - 2).keys()
        ].map(page => page + 1)
      }

      return [
        this.page - 1,
        0,
        ...pages,
        this.totalPages - 1,
        this.page + 1
      ]
    },

    filteredPages () {
      const diff = this.maxVisiblePages / 2
      const toFilterPages = [
        ...Array(this.totalPages).keys()
      ].slice(2, -2)

      if (toFilterPages.length > this.maxVisiblePages) {
        const diffFirst = this.page - toFilterPages[0]
        const diffLast = this.page - toFilterPages[toFilterPages.length - 1]

        if (diffFirst < diff) {
          return toFilterPages.slice(0, this.maxVisiblePages)
        } else if (diffLast >= -diff) {
          return toFilterPages.slice(-this.maxVisiblePages)
        } else {
          return toFilterPages.filter((page) => {
            const diffPage = this.page - page

            return diffPage < 0 ? Math.abs(diffPage) <= diff : diffPage < diff
          })
        }
      }

      return null
    },

    buttons () {
      return this.pages.map((page, key) => {
        return {
          page,
          active: page === this.page,
          disabled: this.disabled(page, key),
          html: this.html(page, key),
          title: this.title(key),
          loading: this.loading && page === this.page
        }
      })
    }
  },

  watch: {
    page: {
      handler: 'pageChange',
      immediate: true
    },

    itemsPerPage: {
      handler: 'rangeChange'
    },

    totalItems: {
      handler: 'rangeChange'
    }
  },

  methods: {
    pageChange (page) {
      if (page >= this.totalPages && page !== 0 && this.totalPages !== 0) {
        throw new Error('page may be maximum the total number of pages minus one')
      }

      this.rangeChange()
    },

    prevPage () {
      if (this.currentPage > 1) {
        this.$emit('page-change', this.currentPage - 1)
      }
    },

    nextPage () {
      if (this.currentPage < this.totalPages) {
        this.$emit('page-change', this.currentPage + 1)
      }
    },

    rangeChange () {
      if (this.page >= this.totalPages && this.totalPages !== 0) {
        this.$emit('page-change', this.totalPages - 1)
      } else {
        this.$emit('range-change', this.start, this.end)
      }
    },

    html (page, key) {
      if (key === 0) {
        return '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"><path fill="none" stroke-linecap="round" stroke-linejoin="round" d="M8.5 11.5L2.5 6l6-5.5" stroke="currentColor"/></svg>'
      }

      if (key === this.pages.length - 1) {
        return '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"><path fill="none" stroke-linecap="round" stroke-linejoin="round" d="M3.5.5l6 5.5-6 5.5" stroke="currentColor"/></svg>'
      }

      if (page === '...') {
        return page
      }

      return (page + 1) + ''
    },

    disabled (page, key) {
      // eslint-disable-next-line no-mixed-operators
      return key === 0 && this.page === 0 || key === this.pages.length - 1 && this.page === this.totalPages - 1 || page === '...'
    },

    title (key) {
      // if (key === 0) {
      //   return 'previous'
      // }

      // if (key === this.pages.length - 1) {
      //   return 'next'
      // }

      return ''
    }
  }
}
</script>

<style lang="scss">
.pagination {
  display: flex;
  justify-items: center;
  justify-content: space-between;
  margin: 35px 0;

  &__mobile-prev-next {
    display: none;
  }

  &__wrapper {
    display: flex;
    justify-items: center;
    justify-content: space-between;
    flex-grow: 1;
  }

  &__info {
    color: #666;
  }

  &__buttons {
    @apply relative z-0 inline-flex shadow-sm;

    .pagination-button:first-child {
      border-top-left-radius: 3px;
      border-bottom-left-radius: 3px;
    }

    .pagination-button:last-child {
      border-top-right-radius: 3px;
      border-bottom-right-radius: 3px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/pagination/pagination-base.vue